<template>
  <div class="order-details-dialog">
    <el-dialog
        v-loading="loading"
        :fullscreen="true"
        :close-on-press-escape="false"
        :show-close="false"
        :close-on-click-modal="false"
        :visible.sync="visible">
      <div
          id="order-details-title"
          class="order-details-title-bar">
        <div class="order-number">
          Return #{{ returnOrderDetails.id }}
        </div>
        <button
            type="button"
            class="el-dialog__headerbtn"
            aria-label="Close"
            @click="$emit('update:visible', false)">
          <i class="el-dialog__close el-icon el-icon-close"></i>
        </button>
      </div>
      <div
          v-if="!loading"
          id="order-details-body"
          class="order-details-body">
        <div class="order-details-timeline">
          <ReturnOrderTimeline
              :return-order="returnOrder" />
        </div>
        <div style="border: 1px solid #DEE5EC;"></div>
        <div
            style="  flex: 3;overflow-y: scroll;display: flex;flex-direction: column;">
          <div
              v-show="generateLabelFailed"
              class="error-message"
              style="background: var(--light-negative-muted, #fcefef); color: var(--light-negative-base, #d4231a);">
            Return label was not generated. Please check the customer address or try again later.
            <button
                type="button"
                class="el-dialog__headerbtn"
                aria-label="Close"
                @click="generateLabelFailed=false">
              <i
                  class="el-dialog__close el-icon el-icon-close"
                  style="color: #d4231a;"></i>
            </button>
          </div>
          <div
              v-show="generateLabelSuccessful"
              class="error-message"
              style="background: var(--light-positive-muted, #F0F8F0); color: var(--light-positive-base, #178019);">
            <template v-if="isNarvarReturn">
              Narvar return label is available.
            </template>
            <template v-else>
              Return label has been generated and sent to {{ returnOrderDetails.customer.email }}.
            </template>
            <a
                v-if="returnLabelUrl"
                :href="returnLabelUrl"
                target="_blank"
                rel="noopener noreferrer">View return label</a>
            <button
                type="button"
                class="el-dialog__headerbtn"
                aria-label="Close"
                @click="generateLabelSuccessful=false">
              <i
                  class="el-dialog__close el-icon el-icon-close"
                  style="color:#178019;"></i>
            </button>
          </div>
          <div
              id="order-details-data"
              class="order-details-data">
            <div class="flex flex-row items-center">
              <div class="order-number">
                Return #{{ returnOrderDetails.id }}
              </div>
              <OrderStatus :order-status="returnOrderDetails.status" />
            </div>
            <div
                class="flex flex-row items-center"
                style="margin: 16px 0">
              <div style="font-weight: 500">
                Date Placed:
              </div>
              <div style="margin-left: 4px">
                <time-zone-formatter
                    output-format="MMM DD, YYYY"
                    :current-time="returnOrderDetails.created_at" />
              </div>
              <div style="font-weight: 500; margin-left: 26px">
                Email:
              </div>
              <div style="margin-left: 4px">
                {{ returnOrderDetails.customer.email }}
              </div>
            </div>
            <div id="order-details-items">
              <return-order-details-item-view
                  v-for="(item, idx) in returnOrderDetails.return_items"
                  :key="idx"
                  v-bind="item"
                  style="margin-bottom: 8px" />
            </div>
            <div
                class="order-details-item"
                style="font-weight: 500; margin-left: 0px; margin-top: 36px;">
              Shipping
            </div>
            <div
                v-if="returnOrderDetails.return_labels.length === 0"
                class="item-details">
              <shipment-details-available-soon />
            </div>
            <div
                v-else
                id="order-details-item">
              <return-order-shipping-view
                  v-for="(item,idx) in returnOrderDetails.return_labels"
                  :key="idx"
                  :tracking-id="item.tracking_id"
                  style="margin-bottom: 8px" />
            </div>
            <div
                class="order-details-item"
                style="font-weight: 500; margin-left: 0px; margin-top: 36px;">
              Payment
            </div>
            <div id="order-details-item">
              <el-card
                  class-name="item-card"
                  shadow="never"
                  style="border-radius: 4px !important; border: 1px solid #dee5ec; margin-right: 12px;"
                  body-style="margin: 8px;">
                <div
                    class="flex flex-row"
                    style="margin: 8px 8px 8px 8px">
                  <div
                      class="flex-1"
                      style="margin: 7px">
                    <div class="item-id flex flex-row">
                      <div class="flex-1">
                        Additional fees
                      </div>
                      <div>{{ formattedAmount( additional_fees.amount, additional_fees.currency_code) }}</div>
                    </div>
                    <el-divider />
                    <div
                        class="item-name flex flex-row">
                      <div class="flex-1 flex">
                        <div>
                          Total refund
                        </div>
                      </div>
                      <div>
                        {{ formattedAmount(returnOrderDetails.refund_info.amount, returnOrderDetails.refund_info.currency) }}
                      </div>
                    </div>
                  </div>
                </div>
              </el-card>
            </div>
            <div
                class="order-details-item flex flex-row"
                style="font-weight: 500">
              <div class="flex-1 flex">
                <div style="font-weight: 500; margin-left: 0px; margin-top: 24px;">
                  Customer Info
                </div>
              </div>
            </div>
            <div
                id="order-details-items">
              <el-card
                  class="item-card"
                  shadow="never"
                  style="border-radius: 4px !important; border: 1px solid #dee5ec;"
                  body-style="margin: 0px;">
                <div
                    class="flex flex-row"
                    style="margin: 8px 24px 8px 8px">
                  <div
                      class="flex-1"
                      style="margin: 8px">
                    <div
                        class="flex flex-row"
                        style="margin: 0px 0px 8px 0px">
                      <div class="flex-1">
                        <strong>Delivery Address</strong>
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Name
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.name }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Phone number
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.phone }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div
                          class="flex-1"
                          style="margin: 0px 0px 24px 0px">
                        Email
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.email }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Street
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.address_1 }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        City
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.city }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        State/province/area
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.state }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Zip code
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.zip }}
                      </div>
                    </div>
                    <div class="flex flex-row">
                      <div class="flex-1">
                        Country
                      </div>
                      <div class="item-price">
                        {{ returnOrderDetails.customer.country }}
                      </div>
                    </div>
                  </div>
                </div>
              </el-card>
            </div>
          </div>
        </div>
      </div>
      <div
          id="order-details-footer"
          class="order-details-footer">
        <el-dropdown
            trigger="click"
            @command="handleAction">
          <el-button
              plain
              class="choose_action_button">
            Choose Action <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu
              slot="dropdown"
              style="max-height: unset !important; overflow: unset !important;">
            <el-dropdown-item
                class="choose_action_item"
                command="resend_label">
              Resend Label
            </el-dropdown-item>
            <el-tooltip
                :disabled="!isNarvarReturn"
                content="Managed by Narvar"
                placement="left">
              <span>
                <el-dropdown-item
                    :disabled="isNarvarReturn || !returnOrderDetails.closable"
                    class="choose_action_item action_cancel"
                    command="cancel_return">
                  Cancel Return
                </el-dropdown-item>
              </span>
            </el-tooltip>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </el-dialog>
    <ConfirmCancel
        v-if="showCancelConfirmation"
        :visible.sync="showCancelConfirmation"
        @cancel-return="confirmCancel" />
  </div>
</template>

<script>
import axios from "axios";
import OrderStatus from "../OrderStatus.vue";
import ReturnOrderDetailsItemView from "./ReturnOrderDetailsItemView.vue";
import ShipmentDetailsAvailableSoon from "../ShipmentDetailsAvailableSoon";
import ReturnOrderShippingView from "./ReturnOrderShippingView.vue";
import numeral from "numeral";
import ConfirmCancel from "./ConfirmCancel.vue";
import ReturnOrderTimeline from "./ReturnOrderTimeline.vue";
import "../../../../assets/order_details.css";
import { createNamespacedHelpers as h } from "vuex";
const { mapActions: requestActions, mapGetters: requestGetters } =
  h("db/requests");

export default {
  name: "ReturnOrderDetails",
  components: {
    ConfirmCancel,
    ReturnOrderShippingView,
    ShipmentDetailsAvailableSoon,
    ReturnOrderDetailsItemView,
    OrderStatus,
    ReturnOrderTimeline,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      required: true,
    },
    returnId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: true,
      returnOrderDetails: {
        customer: { email: "" },
        refund_info: { amount: 0, currency: "USD" },
        return_items: [],
        return_labels: [],
        products: [],
        history: [],
        status: "",
        closable: false,
      },
      showCancelConfirmation: false,
      currencySymbols: {
        EUR: "€",
        NONE: "",
        USD: "$",
        GBP: "£",
        AUD: "A$",
        NZD: "N$",
        CAD: "C$",
      },
      additional_fees: {
        currency_code: "",
        amount: 0,
      },
      returnOrder: { return_order: { history: [] } },
      fetchedReturnSource: null,
      returnLabelUrl: "",
      generateLabelSuccessful: false,
      generateLabelFailed: false,
    };
  },
  computed: {
    isNarvarReturn() {
      return this.fetchedReturnSource === "narvar";
    },
  },
  mounted() {
    this.getReturnOrderDetails();
  },
  methods: {
    formatStatus(status) {
      if (typeof status !== "string") return "";
      return status
        .replace(/_/g, " ")
        .split(" ")
        .filter((word) => word)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    getReturnOrderDetails() {
      this.loading = true;
      return axios
        .get(`v3/returns/return_order_details`, {
          params: {
            order_id: this.order.order.displayable_order_id,
            id: this.returnId,
          },
        })
        .then((response) => {
          const responseData = response && response.data ? response.data : {};
          const rawReturnOrder = responseData.return_order || {};
          const customer = rawReturnOrder.customer || {};
          const refundInfo = rawReturnOrder.refund_info || {};
          const additionalFeesSet = refundInfo.total_additional_fees_set || {};
          const shopMoney = additionalFeesSet.shop_money || {};
          const returnItems = Array.isArray(rawReturnOrder.return_items)
            ? rawReturnOrder.return_items
            : [];
          const returnLabels = Array.isArray(rawReturnOrder.return_labels)
            ? rawReturnOrder.return_labels
            : [];
          const products = Array.isArray(rawReturnOrder.products)
            ? rawReturnOrder.products
            : [];
          const history = Array.isArray(rawReturnOrder.history)
            ? rawReturnOrder.history
            : [];

          this.fetchedReturnSource =
            rawReturnOrder.source === "narvar" ? "narvar" : null;
          this.additional_fees = {
            amount: shopMoney.amount == null ? 0 : shopMoney.amount,
            currency_code: shopMoney.currency_code || "",
          };
          this.returnOrderDetails = {
            ...rawReturnOrder,
            customer: {
              ...customer,
              email: typeof customer.email === "string" ? customer.email : "",
            },
            refund_info: {
              ...refundInfo,
              amount: refundInfo.amount == null ? 0 : refundInfo.amount,
              currency: refundInfo.currency || "USD",
              total_additional_fees_set: {
                ...additionalFeesSet,
                shop_money: shopMoney,
              },
            },
            return_items: returnItems,
            return_labels: returnLabels,
            products,
            history,
            status: this.formatStatus(rawReturnOrder.status),
            closable: Boolean(rawReturnOrder.closable),
          };
          this.returnOrder = {
            ...responseData,
            return_order: this.returnOrderDetails,
          };

          let normalizedReturnItems = [];
          for (let returnItem of returnItems) {
            if (Array.isArray(returnItem.bundle_items)) {
              normalizedReturnItems = normalizedReturnItems.concat(
                this.getNonBundleItems(returnItem)
              );
            } else {
              const item = this.getReturnProductDetails(returnItem || {});
              const quantity = Math.max(Number(returnItem.quantity) || 1, 1);
              for (let i = 0; i < quantity; i++) {
                normalizedReturnItems.push(item);
              }
            }
          }
          this.returnOrderDetails.return_items = normalizedReturnItems;
        })
        .catch((error) => {
          console.log(error);
          this.$message({
            type: "error",
            message: `Can't fetch return order details data. Try again.`,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    ...requestActions(["generateReturnLabel"]),
    handleAction(command) {
      if (command === "resend_label") {
        this.getReturnOrderLabel();
      } else if (command === "cancel_return") {
        this.showCancelConfirmation = true;
      }
    },
    getLabelUrl(responseData) {
      const returnOrder =
        responseData && responseData.return_order
          ? responseData.return_order
          : {};
      const returnLabels = Array.isArray(returnOrder.return_labels)
        ? returnOrder.return_labels
        : [];
      const labelWithUrl = returnLabels.find((label) => {
        if (!label || typeof label.url !== "string") return false;
        return /^https:\/\//i.test(label.url.trim());
      });
      return labelWithUrl ? labelWithUrl.url.trim() : "";
    },
    getReturnOrderLabel() {
      this.loading = true;
      return axios
        .post(`v3/returns/resend_label`, {
          order_id: this.order.order.displayable_order_id,
          return_order_id: this.returnId,
          source: "ND",
        })
        .then((response) => {
          const responseData = response && response.data ? response.data : {};
          this.returnLabelUrl = this.getLabelUrl(responseData);
          this.generateLabelSuccessful = true;
          setTimeout(() => {
            this.generateLabelSuccessful = false;
          }, 10000);
        })
        .catch((error) => {
          const responseData = error && error.response && error.response.data;
          const fallback =
            "Return label was not generated. Please check the customer address or try again later.";
          this.generateLabelFailed = true;
          this.$message({
            type: "error",
            message: (responseData && responseData.message) || fallback,
          });
          setTimeout(() => {
            this.generateLabelFailed = false;
          }, 10000);
        })
        .finally(() => this.getReturnOrderDetails());
    },
    formattedAmount(amount, currency) {
      let numericAmount = Number(amount);
      if (!Number.isFinite(numericAmount)) numericAmount = 0;
      const neg = numericAmount < 0;
      if (neg) numericAmount = -numericAmount;
      const formatted = numeral(numericAmount).format(`0,0.00`);
      return `${neg ? "– " : ""}${
        this.currencySymbols[currency || "USD"] || ""
      }${formatted}`;
    },
    getNonBundleItems(return_item) {
      let return_items = [];
      const items = Array.isArray(return_item.bundle_items)
        ? return_item.bundle_items
        : [];
      for (let item of items) {
        const productDetails = this.getReturnProductDetails(item || {});
        const quantity = Math.max(Number(item && item.quantity) || 1, 1);
        for (let i = 0; i < quantity; i++) {
          return_items.push(productDetails);
        }
      }
      return return_items;
    },
    confirmCancel() {
      this.loading = true;
      return axios
        .post(`v3/returns/close_return`, {
          order_id: this.order.order.displayable_order_id,
          return_order_id: this.returnId,
          source: "ND",
        })
        .then(() => {
          this.$message({
            type: "success",
            message: `Return cancelled successfully!`,
          });
        })
        .catch((error) => {
          const responseData = error && error.response && error.response.data;
          this.$message({
            type: "error",
            message:
              (responseData && responseData.message) ||
              "Return could not be cancelled.",
          });
        })
        .finally(() => {
          this.showCancelConfirmation = false;
          return this.getReturnOrderDetails();
        });
    },
    getReturnProductDetails(return_item) {
      const item = {
        title: "",
        sku: return_item.sku || "",
        images: "",
        status: this.returnOrderDetails.status || "",
      };
      const products = Array.isArray(this.returnOrderDetails.products)
        ? this.returnOrderDetails.products
        : [];
      for (let product of products) {
        if (product && product.sku === return_item.sku) {
          item.title = product.title || "";
          item.images = product.images || "";
          item.id = product.id;
          break;
        }
      }
      return item;
    },
  },
};
</script>

<style>
.error-message {
  display: flex;
  width: 1068px;
  padding: 16px 40px;
  align-items: flex-start;
  gap: 16px;
  font-feature-settings: "clig" off, "liga" off;
  font-family: Equip;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
}
</style>
